import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { mraidSimulationScript } from '../../../../../util/helpers';

const LOAD_TIMEOUT_MS = 15000;

interface IframePreviewProps {
  watchedTag: string;
  selectedSize: string | null;
  iframeRef: React.MutableRefObject<HTMLIFrameElement | null>;
  onIframeLoad?: () => void;
  showSnackBar?: (message: string, severity: 'success' | 'warning' | 'error' | 'info') => void;
}

export default function IframePreview({
  watchedTag,
  selectedSize,
  iframeRef,
  onIframeLoad,
  showSnackBar,
}: IframePreviewProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const checkForDCMClicks = useCallback((doc: Document) => {
    const clickElements = doc.querySelectorAll('a[href*="doubleclick.net/pcs/click"]');

    return Array.from(clickElements).some(el => {
      const href = el.getAttribute('href');
      if (!href) return false;

      try {
        const url = new URL(href);
        const adurl = url.searchParams.get('adurl');
        if (!adurl) return false;

        const parts = adurl.split(/%[^&=]+/);
        return parts.some(part => {
          if (!part) return false;

          try {
            const decodedPart = decodeURIComponent(part);
            if (decodedPart.includes('|') || decodedPart.includes('#')) {
              showSnackBar?.('Warning: Found invalid character (| or #) in tag URL', 'warning');
              return true;
            }
          } catch {
            return false;
          }
          return false;
        });
      } catch (error) {
        showSnackBar?.(`Error checking DCM clicks: ${error.message}`, 'error');
        return false;
      }
    });
  }, [showSnackBar]);

  const checkNestedIframes = useCallback((doc: Document): boolean => {
    const iframes = doc.getElementsByTagName('iframe');

    return Array.from(iframes).some(iframe => {
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc) {
          if (checkForDCMClicks(iframeDoc)) return true;
          if (checkNestedIframes(iframeDoc)) return true;
        }
      } catch {
        return false;
      }
      return false;
    });
  }, [checkForDCMClicks]);

  // Strip third-party monitoring/verification scripts that serve no purpose in preview
  // and can cause performance issues (frame-walking, heavy DOM inspection, etc.)
  const sanitizeTagForPreview = useCallback((tag: string): string =>
    tag
      // Remove DoubleVerify script tags (static <script src="...doubleverify...">...</script>)
      .replace(/<script[^>]*doubleverify\.com[^>]*>[\s\S]*?<\/script>/gi, '')
      // Remove DoubleVerify noscript fallbacks
      .replace(/<noscript>[\s\S]*?doubleverify\.com[\s\S]*?<\/noscript>/gi, ''),
  []);

  const getIframeContent = useCallback(() => {
    if (!watchedTag || !selectedSize) {
      return '';
    }

    const isCeltraTag = watchedTag?.includes('celtra.com');
    const cleanedTag = sanitizeTagForPreview(watchedTag);

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        ${isCeltraTag ? `<script>
          // Prevent Celtra's universal.js from walking up to parent window and reading its dimensions.
          // Create a fake parent that blocks document access but preserves postMessage.
          // Celtra walks frames via: for(;e!==e.parent && e.parent.document;) e=e.parent
          // By returning undefined for .document, the loop exits immediately.
          (function() {
            try {
              var realParent = window.parent;
              var fakeParent = { postMessage: realParent.postMessage.bind(realParent) };
              Object.defineProperty(window, 'parent', { get: function() { return fakeParent; }, configurable: false });
              Object.defineProperty(window, 'top', { get: function() { return fakeParent; }, configurable: false });
              window.__realParent = realParent;
            } catch(e) {}
          })();
        </script>` : ''}
        <script src="https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js"></script>
        <script type="text/javascript">
          function setupMraid() {
            ${mraidSimulationScript}
          }

          const originalCreateElement = document.createElement;
          document.createElement = function(tagName) {
            const element = originalCreateElement.call(document, tagName);
            if (tagName === 'script' && element.src.includes('doubleverify')) {
              element.src = '';
            }
            return element;
          };
        </script>
        <style>
          html, body {
            margin: 0;
            padding: 0;
            overflow: hidden;
            background: #ffffff;
            width: ${selectedSize ? `${selectedSize.split('x')[0]}px` : "100%"};
            height: ${selectedSize ? `${selectedSize.split('x')[1]}px` : "100%"};
          }
          * {
            transform-origin: top left;
          }
          ${isCeltraTag ? `div[class^="celtra-ad-v"] {
            width: ${selectedSize ? `${selectedSize.split('x')[0]}px` : "100%"} !important;
            height: ${selectedSize ? `${selectedSize.split('x')[1]}px` : "100%"} !important;
            overflow: hidden !important;
            position: relative !important;
          }` : ''}
        </style>
      </head>
      <body>
        <div id="creative-container">${cleanedTag}</div>
        <script>
          setupMraid();

          function waitForInit() {
            return new Promise((resolve) => {
              function checkReady() {
                if (window.mraid && mraid.getState() === 'ready') {
                  resolve();
                } else {
                  setTimeout(checkReady, 50);
                }
              }
              checkReady();
            });
          }

          window.addEventListener('message', async function(event) {
            if (event.data === 'capture') {
              try {
                await waitForInit();
                await new Promise(resolve => setTimeout(resolve, 1000));

                const container = document.body;
                const canvas = await html2canvas(container, {
                  logging: false,
                  useCORS: true,
                  allowTaint: true,
                  backgroundColor: '#ffffff',
                  scale: 2,
                  windowWidth: container.scrollWidth,
                  windowHeight: container.scrollHeight,
                  foreignObjectRendering: false,
                  removeContainer: true,
                  onclone: function(clonedDoc) {
                    const clonedBody = clonedDoc.body;
                    clonedBody.style.width = '${selectedSize ? `${selectedSize.split('x')[0]}px` : "100%"}';
                    clonedBody.style.height = '${selectedSize ? `${selectedSize.split('x')[1]}px` : "100%"}';
                    clonedBody.style.transform = 'none';
                  }
                });

                const dataUrl = canvas.toDataURL('image/png', 1.0);
                window.parent.postMessage({ type: 'captureComplete', data: dataUrl }, '*');
              } catch (error) {
                window.parent.postMessage({
                  type: 'captureError',
                  error: error.message || 'Failed to capture content'
                }, '*');
              }
            }
          });

          document.addEventListener('DOMContentLoaded', function() {
            window.parent.postMessage('iframeLoaded', '*');
          });
        </script>
      </body>
      </html>
    `;
  }, [watchedTag, selectedSize, sanitizeTagForPreview]);

  // New function to handle size updates
  const updateIframeSize = useCallback(() => {
    if (!iframeRef.current || !watchedTag || !selectedSize) {
      return;
    }

    try {
      const [width, height] = selectedSize.split('x');
      const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;

      if (iframeDoc) {
        const container = iframeDoc.body;
        if (container) {
          container.style.width = `${width}px`;
          container.style.height = `${height}px`;
        }
      }
    } catch (error) {
      showSnackBar?.(`Error updating size: ${error.message}`, 'error');
    }
  }, [selectedSize, watchedTag, iframeRef, showSnackBar]);

  // Modified setupIframe without selectedSize dependency
  const setupIframe = useCallback(() => {
    if (!iframeRef.current || !watchedTag) {
      return;
    }

    try {
      setIsLoading(true);
      setLoadError(null);

      // Clear any existing timeout
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      // Start loading timeout — kill the iframe if it doesn't load in time
      timeoutRef.current = setTimeout(() => {
        const iframe = iframeRef.current;
        if (iframe) {
          // eslint-disable-next-line no-param-reassign
          iframe.src = 'about:blank';
        }
        setIsLoading(false);
        setLoadError('Preview timed out — the ad tag may be unresponsive.');
      }, LOAD_TIMEOUT_MS);

      const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
      if (!iframeDoc) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        showSnackBar?.('Unable to access iframe document', 'error');
        return;
      }

      iframeDoc.open();
      iframeDoc.write(getIframeContent());
      iframeDoc.close();

      iframeRef.current.addEventListener('load', () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setIsLoading(false);
        setIsInitialized(true);
        if (onIframeLoad) {
          onIframeLoad();
        }

        if (iframeRef.current?.contentDocument) {
          checkForDCMClicks(iframeRef.current.contentDocument);
          checkNestedIframes(iframeRef.current.contentDocument);
        }
      });

    } catch (error) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      showSnackBar?.(`Error setting up preview: ${error.message}`, 'error');
      setIsLoading(false);
    }
  }, [
    iframeRef,
    watchedTag,
    getIframeContent,
    onIframeLoad,
    checkForDCMClicks,
    checkNestedIframes,
    showSnackBar
  ]);

  const handleRetry = useCallback(() => {
    setLoadError(null);
    setIsInitialized(false);
  }, []);

  // Clean up timeout on unmount
  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  // Effect for initial setup
  useEffect(() => {
    if (!isInitialized && watchedTag) {
      setupIframe();
    }
  }, [setupIframe, isInitialized, watchedTag]);

  // Separate effect for handling size changes
  useEffect(() => {
    if (isInitialized && selectedSize && watchedTag) {
      updateIframeSize();
    }
  }, [selectedSize, updateIframeSize, isInitialized, watchedTag]);

  if (!watchedTag || !selectedSize) {
    return null;
  }

  const [width, height] = selectedSize.split('x');

  return (
    <div style={{
      width: `${width}px`,
      height: `${height}px`,
      border: '1px solid #ddd',
      borderRadius: '4px',
      backgroundColor: 'white',
      position: 'relative'
    }}>
      <iframe
        ref={iframeRef}
        title="Creative Preview"
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          visibility: isLoading || loadError ? 'hidden' : 'visible'
        }}
        sandbox="allow-scripts allow-same-origin allow-modals allow-popups allow-forms allow-downloads"
        allow="encrypted-media"
      />
      {isLoading && !loadError && (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'background.paper',
            opacity: 0.8,
            zIndex: 1
          }}
        >
          <CircularProgress />
        </Stack>
      )}
      {loadError && (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'background.paper',
            zIndex: 1,
            p: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" color="error">{loadError}</Typography>
          <Button size="small" variant="outlined" onClick={handleRetry}>Retry</Button>
        </Stack>
      )}
    </div>
  );
}
